/**
 * Shared org-settings wiring for opt-in order document settings forms.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */
import { type ReactNode, useEffect, useState } from "react";

import type { z } from "zod";
import { InfoAlert } from "~/components/Alert";
import { Form } from "~/components/Form";
import { useAuthorization } from "~/contexts/authorizationProvider";
import type { OrderDocumentSlug } from "~/shared/documents/order-document-settings";
import { DocumentsSettingsPermission } from "~/shared/types/RBACPermission";
import { LoadingOverlay, type UseFormReturnType, useForm, zodResolver } from "~/ui";
import { api } from "~/utils/api";
import { onErrorNotification, onSuccessNotification } from "~/utils/mutations";

type UseOptInOrderDocumentSettingsFormArgs<TSchema extends z.ZodTypeAny> = {
  slug: OrderDocumentSlug;
  schema: TSchema;
  emptyConfig: z.infer<TSchema>;
  saveErrorTitle: string;
  saveSuccessTitle: string;
  infoAlert: ReactNode;
  renderFields: (form: UseFormReturnType<z.infer<TSchema>>, canEdit: boolean) => ReactNode;
};

export const useOptInOrderDocumentSettingsForm = <TSchema extends z.ZodTypeAny>({
  slug,
  schema,
  emptyConfig,
  saveErrorTitle,
  saveSuccessTitle,
  infoAlert,
  renderFields,
}: UseOptInOrderDocumentSettingsFormArgs<TSchema>) => {
  const [formKey, setFormKey] = useState(0);
  const settingsQuery = api.org.getOrderDocumentSettings.useQuery({ slug });
  const apiContext = api.useContext();
  const updateMutation = api.org.updateOrderDocumentSettings.useMutation({
    onError: onErrorNotification({ title: saveErrorTitle }),
    onSuccess: onSuccessNotification({ title: saveSuccessTitle }, () => {
      void apiContext.org.retrieveSettings.invalidate();
    }),
  });
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission([DocumentsSettingsPermission.update]);

  type FormValues = z.infer<TSchema>;

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: emptyConfig,
  });

  useEffect(() => {
    if (!settingsQuery.data) {
      return;
    }
    // Query is slug-scoped; tRPC infers a union across all order-document config shapes.
    form.setValues(settingsQuery.data as FormValues);
    setFormKey(k => k + 1);
  }, [settingsQuery.data]);

  const handleSubmit = (values: FormValues) => {
    updateMutation.mutate({ slug, ...values });
  };

  const isLoading = settingsQuery.isPending || settingsQuery.isFetching || updateMutation.isPending;

  const formElement = (
    <Form form={form} onSubmit={handleSubmit} loading={isLoading} mutationError={updateMutation.error} disabled={!canEdit} key={formKey}>
      <LoadingOverlay visible={isLoading} />
      <InfoAlert>{infoAlert}</InfoAlert>
      {renderFields(form, canEdit)}
    </Form>
  );

  return { formElement, form, canEdit, isLoading };
};
